import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";
import "react-phone-input-2/lib/style.css";
import "./styles.css";
import { useAuth } from "../../context/Auth";

const COUNTRY_LOOKUP_CACHE_KEY = "country-phone-input-ip-country";
const FALLBACK_COUNTRY_CODE = "in";
const COUNTRY_LOOKUP_API = "https://ipapi.co/json/";

export const CountryPhoneInput = ({
  value,
  onChange,
  defaultCountryCode = "",
  defaultMobileCode = "",
  numberPlaceholder = "Phone Number",
  canShowHint = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [detectedCountryCode, setDetectedCountryCode] = useState("");
  const { otpModalOpen } = useAuth();

  useEffect(() => {
    if (otpModalOpen) {
      setIsOpen(false);
      setSearchValue("");
    }
  }, [otpModalOpen]);
  const [hasResolvedCountryCode, setHasResolvedCountryCode] = useState(
    Boolean(value?.mobile_country_code),
  );
  const dropdownRef = useRef(null);

  const toFlagEmoji = (iso2) => {
    if (!iso2 || iso2.length !== 2) return "🌐";
    const codePoints = iso2
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  const countryOptions = useMemo(() => {
    const displayNames =
      typeof Intl !== "undefined" && Intl.DisplayNames
        ? new Intl.DisplayNames(["en"], { type: "region" })
        : null;

    return getCountries()
      .map((iso2) => ({
        iso2: iso2.toLowerCase(),
        name: displayNames?.of(iso2.toUpperCase()) || iso2.toUpperCase(),
        dialCode: `+${getCountryCallingCode(iso2)}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const getCountryByIso2 = useCallback(
    (iso2) => {
      if (!iso2) return null;
      return countryOptions.find((c) => c.iso2 === iso2.toLowerCase()) || null;
    },
    [countryOptions],
  );

  useEffect(() => {
    if (value?.mobile_country_code) {
      setDetectedCountryCode("");
      setHasResolvedCountryCode(true);
      return;
    }

    let ignore = false;

    const resolveCountryCode = (iso2) => {
      if (ignore) return;

      const normalizedIso2 = iso2?.toLowerCase();
      const validCountry = getCountryByIso2(normalizedIso2)
        ? normalizedIso2
        : (defaultCountryCode || FALLBACK_COUNTRY_CODE).toLowerCase();

      setDetectedCountryCode(validCountry);
      setHasResolvedCountryCode(true);

      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(COUNTRY_LOOKUP_CACHE_KEY, validCountry);
      }
    };

    const cachedCountryCode =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem(COUNTRY_LOOKUP_CACHE_KEY)
        : "";

    if (cachedCountryCode && getCountryByIso2(cachedCountryCode)) {
      resolveCountryCode(cachedCountryCode);
      return () => {
        ignore = true;
      };
    }

    fetch(COUNTRY_LOOKUP_API)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to detect country from IP");
        }
        return response.json();
      })
      .then((data) => {
        resolveCountryCode(data?.country_code);
      })
      .catch(() => {
        resolveCountryCode(defaultCountryCode || FALLBACK_COUNTRY_CODE);
      });

    return () => {
      ignore = true;
    };
  }, [defaultCountryCode, getCountryByIso2, value?.mobile_country_code]);

  const selectedCountry = (() => {
    const iso2 = (value?.mobile_country_code || "").trim();
    // 1. Use stored ISO2 only if it's a valid 2-letter code that resolves to a known country
    if (iso2.length === 2) {
      const found = getCountryByIso2(iso2);
      if (found) return found;
    }
    // 2. Derive country from dial code (e.g. "+61" → Australia) when country code is missing/invalid
    if (value?.mobile_code) {
      const byDialCode = countryOptions.find((c) => c.dialCode === value.mobile_code);
      if (byDialCode) return byDialCode;
    }
    // 3. Fall back to IP-detected, default, or India
    return (
      getCountryByIso2(detectedCountryCode || defaultCountryCode || FALLBACK_COUNTRY_CODE) ||
      getCountryByIso2(FALLBACK_COUNTRY_CODE)
    );
  })();

  const dialCode =
    value?.mobile_code || selectedCountry?.dialCode || defaultMobileCode || "+";

  useEffect(() => {
    if (!selectedCountry) return;
    const needsCountryCode = !value?.mobile_country_code;
    const needsDialCode = !value?.mobile_code;
    if (!needsCountryCode && !needsDialCode) return;
    if (needsCountryCode && !hasResolvedCountryCode) return;

    onChange?.({
      ...value,
      mobile_country_code: (
        value?.mobile_country_code || selectedCountry.iso2
      ).toUpperCase(),
      mobile_code: value?.mobile_code || selectedCountry.dialCode,
    });
  }, [hasResolvedCountryCode, onChange, selectedCountry, value]);

  const handleCountryChange = (event) => {
    const iso2 = event.target.value;
    const countryData = getCountryByIso2(iso2);
    if (!countryData) return;

    onChange?.({
      ...value,
      mobile_country_code: iso2.toUpperCase(),
      mobile_code: countryData.dialCode,
    });
  };

  const filteredOptions = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return countryOptions;
    return countryOptions.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.dialCode.includes(query) ||
        c.iso2.includes(query),
    );
  }, [countryOptions, searchValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current || dropdownRef.current.contains(event.target))
        return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNumberChange = (event) => {
    // Strip everything except digits, then remove leading zeros
    const next = event.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "");
    onChange?.({
      ...value,
      mobile_number: next,
    });
  };

  // Sanitise: strip spaces/hyphens and leading zeros before validation
  const sanitisedNumber = (value?.mobile_number || "").replace(/[^0-9]/g, "").replace(/^0+/, "");
  const fullNumber = `${dialCode}${sanitisedNumber}`;
  const hasNumber = sanitisedNumber.length > 0;
  const isNumberValid = hasNumber ? isValidPhoneNumber(fullNumber) : true;

  return (
    <div className="country-phone-input">
      <div className="country-phone-input__row">
        <div ref={dropdownRef} className="country-phone-input__selector">
          <button
            type="button"
            className="style2-input form-control text-grey-900 font-xsss fw-600 country-phone-input__button"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <span className="country-phone-input__flag">
              {toFlagEmoji(selectedCountry?.iso2 || "in")}
            </span>
            <span className="country-phone-input__dial">{dialCode}</span>
            {/* <span className="country-phone-input__name">{selectedCountry?.name || ""}</span> */}
            <span className="country-phone-input__caret" aria-hidden="true">
              <i className="fa-sharp fa-solid fa-angle-down" />
            </span>
          </button>

          {isOpen && (
            <div className="country-phone-input__dropdown">
              <input
                type="text"
                className="style2-input form-control text-grey-900 font-xsss fw-600 country-phone-input__search"
                placeholder="Search country"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <div className="country-phone-input__list">
                {filteredOptions.map((c) => (
                  <button
                    key={c.iso2}
                    type="button"
                    onClick={() => {
                      handleCountryChange({ target: { value: c.iso2 } });
                      setIsOpen(false);
                      setSearchValue("");
                    }}
                    className="country-phone-input__option"
                  >
                    <span className="country-phone-input__flag">
                      {toFlagEmoji(c.iso2)}
                    </span>
                    <span className="country-phone-input__dial">
                      {c.dialCode}
                    </span>
                    <span className="country-phone-input__name">{c.name}</span>
                  </button>
                ))}
                {filteredOptions.length === 0 && (
                  <div className="text-grey-500 font-xsss country-phone-input__empty">
                    No matching countries
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="country-phone-input__number-wrap">
          <input
            type="tel"
            style={hasNumber && !isNumberValid ? {borderTopRightRadius:0} : {}}
            className="style2-input form-control text-grey-900 font-xsss fw-600 country-phone-input__number"
            placeholder={numberPlaceholder}
            value={value?.mobile_number || ""}
            onChange={handleNumberChange}
          />
          {hasNumber && !isNumberValid && (
            <span className="country-phone-input__status-tooltip-wrap">
              <span
                className="country-phone-input__status-icon country-phone-input__status-icon--error country-phone-input__status-icon--inside"
                aria-label="Invalid mobile number"
              >
                <i
                  className="fa-sharp fa-light fa-circle-xmark"
                  aria-hidden="true"
                />
              </span>
              <span className="country-phone-input__tooltip">
                Please enter a valid mobile number.
              </span>
            </span>
          )}
          {hasNumber && isNumberValid && (
            <span
              className="country-phone-input__status-icon country-phone-input__status-icon--success country-phone-input__status-icon--inside"
              aria-label="Valid mobile number"
            >
              {/* <i className="fa-light fa-badge-check" aria-hidden="true" /> */}
            </span>
          )}
        </div>
      </div>
      {canShowHint && (
        <div>
          <div className="country-phone-input__hint">
            International Format. Remove the leading 0 (e.g. 4123 not 0412)
            {/* Mobile Number (International format){" "}
            <span className="country-phone-input__required">*</span>
            <span className="country-phone-input__hint-muted">
              {" "}
              Remove any leading 0 (e.g. 4123, not 04123)
            </span> */}
          </div>
          {/* <div className="country-phone-input__preview">
            International format:{" "}
            <span className="country-phone-input__preview-value">{`${dialCode}${value?.mobile_number || ""}`}</span>
          </div> */}
        </div>
      )}
    </div>
  );
};
